import { useState, useEffect } from "react";
import moment from "moment";
import { debounce } from "lodash";

import { AutoComplete, Button, Form, Input, Popconfirm, Select, Space, Spin, Table, TimePicker } from "antd";
import { CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined, StopOutlined } from "@ant-design/icons";

import notifier from "utils/notifier";
import { toLetters, validateCharges } from "utils/helpers";
import { ErrorResponse } from "utils/types";

import { EditableCellProps, Item, ProcedureTableProps } from "./Proptypes";

const { Option } = Select;

export const ProcedureTable = (props: ProcedureTableProps) => {
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState('');
  const [procedureType, setProcedureType] = useState('');
  const [invalidNdc, setInvalidNdc] = useState(false);
  const [datesVisible, setDatesVisible] = useState(false);
  const [totalTime, setTotalTime] = useState('');
  const [totalTimeInMinutes, setTotalTimeInMinutes] = useState(0);
  const [initialNdcTimes, setInitialNdcTimes] = useState({
    from: '',
    to: '',
  });
  const [cptCodeDropdownKey, setCptCodeDropdownKey] = useState<any>(null);
  const [ndcCodeResult, setNdcCodeResult] = useState<any[]>([]);
  const [ndcRequired, setNdcRequired] = useState(false);
  const [open, setOpen] = useState(false);

  const calculateTotalTime = () => {
    const start = form.getFieldValue('ndcFrom');
    const end = form.getFieldValue('ndcTo');

    if (!start || !end) {
      setTotalTime(`0 hours 0 minutes`);
      setTotalTimeInMinutes(0);
      return;
    }

    let duration = moment.duration(end.diff(start));
    let hours = duration.hours() > 0 ? duration.hours() : 0;
    let minutes = duration.minutes() > 0 ? duration.minutes() : 0;

    setTotalTime(`${hours} hours ${minutes} minutes`);
    setTotalTimeInMinutes(Math.ceil(duration.asMinutes()));
  };

  useEffect(() => {
    getRelatedNdc();
  }, []);

  const handleNdcCodeSearch = (value: string) => {
    if (value.length < 4)  {
      setOpen(false);
      return;
    }
    props.getNdcCodes(value)
      .then((response: any) => {
        setNdcCodeResult(response);

        // Open the AutoComplete dropdown
        setOpen(true);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const handleSelectNdcCode = (value: string, option: any) => {
    let selectedNdc = JSON.parse(value);
    form.setFieldsValue({ ndcCode: selectedNdc.code});
    setOpen(false);
  }

  const tabSelectEvent = (evt: any, searchResult: any[], id: string) => {
    if (searchResult.length > 0 && evt.key === 'Tab') {
      const enterKey = new KeyboardEvent('keydown', {
        code: 'Enter',
        key: 'Enter',
        charCode: 13,
        keyCode: 13,
        view: window,
        bubbles: true
      });

      document.getElementById(id)!.dispatchEvent(enterKey);
    }
  }

  const setCptValidation = (cpt: string) => {
    props.checkIfNdcIsRequired(cpt)
      .then((response: any) => {
        setNdcRequired(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });


  }

  const EditableCell: React.FC<EditableCellProps> = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {

    const inputNode = (type: string) => {
      switch (type) {
        case 'RmiDetailNote':
          return;
        case 'info':
          return (
            <>
              <Form.Item
                name="procedure"
              >
              <Select
                value={procedureType}
                onSelect={e => setProcedureType(e)}
              >
                <Option value="NDC">NDC</Option>
                <Option value="ZZ">ZZ</Option>
              </Select>
              </Form.Item>
              {procedureType !== 'ZZ' ? (
                <>
                  <Form.Item
                    name='ndcCode'
                    dependencies={['ndcQty', 'CPT']}
                    validateTrigger={['onBlur']}
                    validateStatus={invalidNdc ? "error" : "success"}
                    hasFeedback
                    help={invalidNdc && 'Valid NDC Code Required.'}
                    rules={[
                      ({ getFieldValue }: any) => ({
                        async validator(_: any, value: any) {

                          if ( ndcRequired && !value) {
                            setInvalidNdc(true);
                            return Promise.reject(new Error('NDC Code is required.'));
                          }

                          if (!value || value.length === 11) {
                            setInvalidNdc(false);
                            return Promise.resolve();
                          }
                          setInvalidNdc(true);
                          return Promise.reject(new Error('This NDC code is not compatible with the selected CPT Code, please enter another.'));
                        },
                      }),
                    ]}
                  >
                  <AutoComplete
                    key={index}
                    style={{ width: '150px' }}
                    open={open}
                    onSearch={debounce(handleNdcCodeSearch, 500)}
                    onSelect={handleSelectNdcCode}
                    placeholder="Search for NDC Codes..."
                    notFoundContent={'Not found. Please try your search again.'}
                    defaultActiveFirstOption={true}
                    onKeyDown={(e) => tabSelectEvent(e, ndcCodeResult, 'ndc-code')}
                    maxLength={11}
                    allowClear
                    autoFocus
                  >
                    {props.isFetchingNdcCodes ? <Option value=""><Spin /></Option> : ndcCodeResult.map((option: any, key:number) => (
                      <Option key={key} value={JSON.stringify(option)}>
                        {option.codeName}
                      </Option>
                  ))}
                  </AutoComplete>
                  </Form.Item>

                  <div className="row">
                    <Form.Item
                      className="col-lg-14 col-md-24"
                      name="ndcUnit"
                    >
                      <Select>
                        <Option value="ME">ME</Option>
                        <Option value="ML">ML</Option>
                        <Option value="UN">UN</Option>
                        <Option value="GR">GR</Option>
                        <Option value="F2">F2</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      className="col-lg-10 col-md-24"
                      name="ndcQty"
                      dependencies={['ndcCode']}
                    >
                    <Input
                      type="number"
                      onKeyDown={ (evt) => (evt.key === 'e' || evt.key === '+' || evt.key === '-' || evt.key === '.') && evt.preventDefault() }
                    />
                    </Form.Item>
                  </div>
                  {datesVisible && (
                  <>
                    <Form.Item
                      name="ndcFrom"
                    >
                    <TimePicker
                      placeholder="From:"
                      use12Hours
                      showNow
                      onBlur={() => calculateTotalTime()}
                      allowClear={false}
                      style={{ width: '100%' }}
                    />
                    </Form.Item>

                    <Form.Item
                      name="ndcTo"
                      validateStatus={'success'}
                      help={'Total time: ' + totalTime}
                    >
                    <TimePicker
                      placeholder="To:"
                      use12Hours
                      showNow
                      onBlur={() => calculateTotalTime()}
                      allowClear={false}
                      style={{ width: '100%' }}
                    />
                    </Form.Item>
                    <div className="btn-wrapper">
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          form.setFields([
                            { name: 'ndcFrom', value: initialNdcTimes.from },
                            { name: 'ndcTo', value: initialNdcTimes.to }
                          ]);
                          calculateTotalTime();
                        }}
                        style={{ width: '100%', margin: '0 4px 0 0' }}
                      >
                        RESET
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          form.setFields([
                            { name: 'ndcFrom', value: undefined },
                            { name: 'ndcTo', value: undefined },
                            { name: 'ndcTime', value: undefined },
                          ]);
                          setTotalTime('');
                          setTotalTimeInMinutes(0);
                          setDatesVisible(false);
                        }}
                        style={{ width: '100%', margin: '0 4px 0 0' }}
                      >
                        REMOVE
                      </Button>
                    </div>
                </>
                )}
                {!datesVisible && (
                  <div className="btn-wrapper">
                    <Button
                      type="primary"
                      style={{ width: '100%' }}
                      onClick={() => {
                        setDatesVisible(true);
                        calculateTotalTime();
                      }}
                    >
                      ADD NDC TIME
                    </Button>
                  </div>
                )}
                </>
              ):(
                <Form.Item
                  name="additionalServiceInfo"
                >
                  <Input placeholder="Additional Service Info..." />
                </Form.Item>
              )}
            </>
          )
        case 'charges':
          return <Input
                    type="number"
                    prefix="$"
                    onKeyPress={ (e: any) => !validateCharges(e) && e.preventDefault()}
                    onKeyDown={ (evt) => (evt.key === 'e' || evt.key === '+' || evt.key === '-') && evt.preventDefault() }
                  />;

        case 'daysOrUnits':
        case 'providerNPI':
          return <Input
                    type="number"
                    onKeyDown={ (evt) => (evt.key === 'e' || evt.key === '+' || evt.key === '-' || evt.key === '.') && evt.preventDefault() }
                  />;

        case 'modifiers':
          return (
            <>
            <Space align="baseline">
              <Form.Item
                name={'modifiers'}

              >
              <Select
                placeholder={"Modifier 1"}
                showSearch
                style={{ width: '150px' }}
                loading={props.isFetchingModifierCodes}
                allowClear
              >
                {props.modifierCodes.map((value: any) => (
                  <Option key={value.RmiCodeDesc} value={JSON.stringify(value)}>
                    {value.RmiCodeDesc}
                  </Option>
                ))}
              </Select>
              </Form.Item>
              <CloseOutlined onClick={() => form.setFieldsValue({ modifiers: null })} />
            </Space>

            <Form.List name="additionalModifiers">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }, index) => (
                    <Space key={key} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'modifier']}
                        fieldKey={[fieldKey, 'modifier']}

                      >
                      <Select
                        placeholder={"Modifier " + (index + 2)}
                        showSearch
                        style={{ width: '150px' }}
                        loading={props.isFetchingModifierCodes}
                        allowClear
                      >
                        {props.modifierCodes.map((value: any) => (
                          <Option key={value.RmiCodeDesc} value={JSON.stringify(value)}>
                            {value.RmiCodeDesc}
                          </Option>
                        ))}
                      </Select>
                      </Form.Item>
                      <CloseOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item>
                    {fields.length < 3 && (
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Modifier
                      </Button>
                    )}
                  </Form.Item>
                </>
              )}
              </Form.List>
            </>
          );

        case 'diagRef':
          return (
            <>
            <Form.Item
              name="diagRef"
            >
              <Select
                style={{ width: '100px' }}
              >
                {props.diagDataSource.map((_: any, index: number) => (
                  <Option
                    key={index}
                    value={toLetters(index + 1)}
                  >
                    {toLetters(index + 1)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.List name="additionalDiags">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }, index) => (
                    <Space key={key} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'diag']}
                        fieldKey={[fieldKey, 'diag']}

                      >
                      <Select
                        placeholder={"Diag " + (index + 2)}
                        style={{ width: '100px' }}
                      >
                        {props.diagDataSource.map((value: any, index: number) => (
                            <Option
                              key={value.RmiDiagCode}
                              value={JSON.stringify({ ...value, index: index + 1, letterIndex: toLetters(index + 1) })}
                            >
                              {toLetters(index + 1)}
                            </Option>
                          ))}
                      </Select>
                      </Form.Item>
                      <CloseOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item>
                    {fields.length < 3 && (
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Diag
                      </Button>
                    )}
                  </Form.Item>
                </>
              )}
            </Form.List>
            </>
          );

        default:
          return <Input />;
      };
    };

    const required = (title !== 'MODIFIERS' && title !== 'SUPPL. INFO');
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required,
                message: `Please Input ${title}!`,
              },
              () => ({
                async validator(field: any, value: any) {
                  if (value && (field.field === 'daysOrUnits' || field.field === 'charges') && (isNaN(parseInt(value)) || parseInt(value) <= 0)) {
                    return Promise.reject(new Error('Must be greater than zero.'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            {inputNode(inputType)}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  //claim generator final submission page custom summary row
  const summary = {
    key: 'summary',
    fromTo: '',
    POS: '',
    EMG: '',
    modifiers: '',
    diagRef: '',
    procedure: '',
    ndcCode: '',
    ndcUnit: '',
    ndcQty: '',
    ndcTime: '',
    CPT: '',
    RmiCharges: 'summary',
    daysOrUnits: '',
    providerNPI: '',
  };

  const isEditing = (record: Item) => record.key === editingKey;

  const edit = (record: Partial<Item> & { key: React.Key }) => {
    form.setFieldsValue({...record});
    setEditingKey(record.key);
    setDatesVisible(false);
    setProcedureType(record.procedure!);
    setTotalTime(record.ndcTime ? record.ndcTime : '0 hours 0 minutes');
    setInitialNdcTimes({ from: record.ndcFrom ? record.ndcFrom : '', to: record.ndcTo ? record.ndcTo : '' });
    (record.ndcTime && record.ndcTime !== '0 hours 0 minutes') && setDatesVisible(true);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields() as Item);
      const moreInfo = await form.validateFields(['charges', 'daysOrUnits', 'providerNPI', 'ndcTo', 'ndcFrom', 'ndcTime']);

      const newData = [...props.procDataSource];
      const index = newData.findIndex(item => key === item.key);
      if (index > -1) {
        const item = newData[index]
        newData.splice(index, 1, {
          ...item,
          ...row,
          ...moreInfo,
          ndcTime: totalTime,
          ndcTimeInMinutes: totalTimeInMinutes,
        });
        props.setProcDataSource(newData);
        setEditingKey('');
      } else {
        newData.push(row);
        props.setProcDataSource(newData);
        setEditingKey('');
      }
    } catch (errInfo) {
      notifier.error('Validation Failure: Please check your fields and try again');
    }
  };

  const remove = (record: Item, index: number) => {
    // check if the removed is office and is last one
    const selectedPOS = JSON.parse(record.POS);
    if (selectedPOS.RmiCode === '11' || selectedPOS.RmiCode === '02' || selectedPOS.RmiCode === '10') {
      // Facility not required
      props.setPosOffice(true);
    } else {
      // not office and not last
      if (props.procDataSource.length > 1) {
        // Facility required
        props.setPosOffice(false);
      } else {
        // last one, Facility not required
        props.setPosOffice(true);
      }
    }

    props.setProcDataSource((prev: any[]) => prev.filter((value, i) => i !== index));
  };

  //claim generator final submission page
  const getRelatedNdc = async() => {
    let newArr: any[] = [...props.procDataSource];
    newArr.map((proc: any) => {
      props.getRelated(proc.RmiCptCode)
        .then((response: any) => {
          if (response.length !== 0) {
            const foundIndex = newArr.findIndex((arr: any) => arr.RmiCptCode === response[0].RmiCptCode);
            newArr[foundIndex].ndcOptions = response || [];
            return newArr;
          }
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });
    });

    Promise.all(newArr).then((arr: any) => {
      props.setProcDataSource(arr);
    })
    .catch((err: ErrorResponse) =>
      notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.')
    );
  }

  //claim generator final submission page
  const handleSelectedRelatedNdc = (item: any, index: number) => {
    const parsed = JSON.parse(item ||'{}');
    let newArr: any[] = [...props.procDataSource];
    newArr[index].ndcCode = parsed.code;
    newArr[index].RmiDetailNote = 'NDC' + parsed.code;

    props.setProcDataSource(newArr);
  }

  //claim generator final submission page
  const onClearNdc = (index: number) => {
    let newArr: any[] = [...props.procDataSource];
    newArr[index].RmiNdcCode = '';
    newArr[index].RmiDetailNote = '';
    props.setProcDataSource(newArr);
  }

  //claim generator final submission page
  const handleSelectedCPTcode = (fieldName: string, item: any, index: number) => {
    const parsed = JSON.parse(item ||'{}');
    let newArr = props.procDataSource;

    if (parsed.RmiProcCode) {
      form.setFields([{ name: fieldName, value: parsed.RmiProcCode }]);
      newArr[index].RmiCptCode = parsed.RmiProcCode;
      props.setProcDataSource(newArr);
      props.setMissingCPTCode(false);
    }

    setCptCodeDropdownKey(null);
  }

  //claim generator final submission page
  const onClearCPTcode = (index: number) => {
    let newArr = props.procDataSource;
    newArr[index].RmiCptCode = '';
    setCptCodeDropdownKey(null);
    props.setMissingCPTCode(true);
    props.setCPTCodesResult([]);
    props.setProcDataSource(newArr);
  }

  const procColumns = [
    {
      title: editingKey === '' ? 'EDIT/REMOVE' : 'SAVE/CANCEL',
      key: 'edit',
      dataIndex: 'edit',
      width: '5%',
      render: (_: string, record: Item, index: number) => {
        if (record.key === 'summary') return;
        const editable = isEditing(record);
        if (editable) setCptValidation(record.RmiCptCode!);
        return editable ? (
          <span>
            <a onClick={() => save(record.key)}>
              <SaveOutlined />
            </a>
            &nbsp;/&nbsp;
            <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
              <a><StopOutlined /></a>
            </Popconfirm>
          </span>
        ) : (
        <>
          {record.newMeasure !== 'No' &&
          <>
            <a>
              <EditOutlined disabled={editingKey !== ''} onClick={() => edit(record)} />
            </a>
              &nbsp;/&nbsp;
            <a onClick={() => remove(record, index)}>
              <DeleteOutlined />
            </a>
          </>
          }
        </>
        );
      }
    },
    {
      title: 'SUPPL. INFO',
      key: 'info',
      dataIndex: 'info',
      editable: true,
      width: '30%',
      render: (_: string, record: Item) => {
        if (!record.procedure) return '';
        if (record.procedure === 'NDC') return `${record.procedure}${record.ndcCode ? record.ndcCode : ''} ${record.ndcUnit ? record.ndcUnit : ''}${record.ndcQty ? record.ndcQty : ''}`
        else return `${record.procedure} ${record.additionalServiceInfo ? record.additionalServiceInfo : ''}`;
      }
    },
    {
      title: <>FROM<br/>TO</>,
      key: 'RmiDosFrom',
      dataIndex: 'RmiDosFrom',
      width: '7%',
      render: (text: any, record: Item) => {
        //claim generator final submission page custom column
        if (text !== 'summary' && record?.key !== 'summary') {
          if (!record.ndcTime || record.ndcTime === '0 hours 0 minutes') return <>{moment(record.RmiDosFrom).format('MM/DD/YYYY')} {moment(record.RmiDosTo).format('MM/DD/YYYY')}</>;
          else {
            const from = text.props.children[1] + ' @ ' + moment(record.ndcFrom).format('MM/DD/YYYY');
            const to = text.props.children[5] + ' @ ' + moment(record.ndcTo).format('MM/DD/YYYY');
            return <>{!isEditing(record) ? from : text}<br/>{!isEditing(record) && to}<br/>{!isEditing(record) && record.ndcTime}</>;
          }
        }
      }
    },
    {
      title: 'POS',
      key: 'RmiPosCode',
      dataIndex: 'RmiPosCode',
      width: '5%',
    },
    {
      title: 'EMG',
      key: 'RmiEmg',
      dataIndex: 'RmiEmg',
      width: '5%',
    },
    {
      title: 'CPT',
      key: 'RmiCptCode',
      dataIndex: 'RmiCptCode',
      width: '5%',
      render: (text: string, record: Item, index: number) => {
        if (text === 'summary') {
          return (
            <Form.Item
              label={<>Patient Account &nbsp;<span style={{ fontSize: '10px' }}>box 26</span></>}
              name="patientAccount"
              labelCol={{ span: 24 }}
            >
              <Input
                onBlur={e => props.setPatientAccount(e.target.value)}
              />
            </Form.Item>

          );
        } else {
          return text;
        }
      }
    },
    {
      title: 'MODIFIERS',
      key: 'modifiers',
      dataIndex: 'modifiers',
      width: '8%',
      editable: true,
      render: (_: string, record: Item) => {
        return (
          <>
            {record.RmiCptModifier1 && record.RmiCptModifier1 + ' '}
            {record.RmiCptModifier2 && record.RmiCptModifier2 + ' '}
            {record.RmiCptModifier3 && record.RmiCptModifier3 + ' '}
            {record.RmiCptModifier4 && record.RmiCptModifier4 + ' '}
          </>
        );
      }
    },
    {
      title: 'DIAG REF',
      key: 'RmiDiagPtr',
      dataIndex: 'RmiDiagPtr',
      width: '6%',
      editable: true,
      render: (text: string, record: Item) => {
        if (text !== 'summary' && record?.key !== 'summary') {
          return (
            <>
              {record.RmiDiagPtr && toLetters(record.RmiDiagPtr) + ' '}
              {record.RmiDiagPtr2 && toLetters(record.RmiDiagPtr2) + ' '}
              {record.RmiDiagPtr3 && toLetters(record.RmiDiagPtr3) + ' '}
              {record.RmiDiagPtr4 && toLetters(record.RmiDiagPtr4) + ' '}
            </>
          )
        }
      }
    },
    {
      title: 'CHARGES',
      key: 'RmiCharges',
      dataIndex: 'RmiCharges',
      width: '5%',
      editable: true,
      render: (text: string) => {
        if (text === 'summary') {
          let totalCharges = props.procDataSource.reduce((accumulator: any, item: any) => {
            return accumulator + parseFloat(item.RmiCharges);
          }, 0);

          props.setTotalCharges(totalCharges.toString());

          let balance = totalCharges - parseFloat(props.amountPaid);

          props.setBalance(balance.toString());

          return (
            <Form.Item
              label={<>Total Charges &nbsp;<span style={{ fontSize: '10px' }}>box 28</span></>}
              labelCol={{ span: 24 }}
            >
              ${totalCharges.toFixed(2)}
            </Form.Item>
          );
        } else {
          return `$${parseFloat(text).toFixed(2)}`;
        }
      }
    },
    {
      title: 'DAYS or UNITS',
      key: 'RmiDays',
      dataIndex: 'RmiDays',
      width: '10%',
      editable: true,
      render: (text: string | number) => {
        if (text === 'summary') {
          return (
            <Form.Item
              label={<>Amount Paid &nbsp;<span style={{ fontSize: '10px' }}>box 29</span></>}
              name="amountPaid"
              labelCol={{ span: 24 }}
            >
              <Input
                key="amountPaid"
                type="number"
                prefix="$"
                onKeyDown={ (evt) => (evt.key === 'e' || evt.key === '+' || evt.key === '-') && evt.preventDefault() }
                onBlur={e => {
                  if (e.target.value.length === 0 || parseFloat(e.target.value) < 0) {
                    props.setAmountPaid('0');
                    form.setFieldsValue({ amountPaid: 0 });
                  }
                  else props.setAmountPaid(e.target.value);
                }}
              />
            </Form.Item>
          );
        } else {
          return text;
        }
      }
    },
    {
      title: 'PROVIDER NPI',
      key: 'RmiRendProviderNpi',
      dataIndex: 'RmiRendProviderNpi',
      editable: true,
      render: (text: string) => {
        if (text === 'summary') {
          let totalCharges = props.procDataSource.reduce((accumulator: any, item: any) => {
            return accumulator + parseFloat(item.RmiCharges);
          }, 0);

          let balance = totalCharges - parseFloat(props.amountPaid);

          props.setBalance(balance.toString());

          return (
            <>
            Balance
            <br />
            ${balance.toFixed(2)}
            </>
          );
        } else {
          return text;
        }
      }
    },
  ];

  const mergedColumns = procColumns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        inputType: col.dataIndex,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <Form form={form} name="claimForm" component={false}>
      <Table
        {...props}
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        columns={mergedColumns}
        dataSource={[...props.procDataSource, summary]}
        rowClassName="editable-row"
        pagination={false}
        rowKey={"key"}
        style={{
          width: '100%'
        }}
      />
    </Form>
  );
};